import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getFinancialYears, getMe } from '../services/api';
import { useThemeActions } from './ThemeContext';
import {
  getStoredJson,
  getStoredValue,
  removeStoredValue,
  setStoredJson,
  setStoredValue,
} from '../utils/storage';
import {
  getDefaultSelectableFinancialYear,
  getOpeningFinancialYear,
  getVisibleFinancialYears,
} from '../utils/financialYears';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [financialYears, setFinancialYears] = useState([]);
  const [financialYearsLoaded, setFinancialYearsLoaded] = useState(false);
  const [activeFinancialYearIdState, setActiveFinancialYearIdState] = useState(null);
  const [activeDerasarId, setActiveDerasarIdState] = useState(
    () => getStoredValue('activeDerasarId')
  );
  const { applyTheme } = useThemeActions();

  const getStoredFinancialYearMap = () => getStoredJson('activeFinancialYearIdsByDerasar', {});
  const setStoredFinancialYearMap = (value) => setStoredJson('activeFinancialYearIdsByDerasar', value);

  const syncUserState = (nextUser) => {
    setUser(nextUser);
    setStoredJson('user', nextUser);
  };

  const clearUserState = () => {
    removeStoredValue('user');
    removeStoredValue('activeDerasarId');
    removeStoredValue('activeFinancialYearIdsByDerasar');
    setUser(null);
    setActiveDerasarIdState(null);
    setFinancialYears([]);
    setFinancialYearsLoaded(false);
    setActiveFinancialYearIdState(null);
  };

  useEffect(() => {
    const stored = getStoredJson('user');
    if (stored) {
      setUser(stored);
      getMe()
        .then((profile) => {
          const updated = { ...profile, token: stored.token };
          const current = getStoredJson('user', {});
          if (current.isEmailVerified) updated.isEmailVerified = true;
          syncUserState(updated);

          if (updated.theme) {
            applyTheme(updated.theme);
          }

          const approvedDerasars = (updated.derasars || []).filter(
            (d) => d.status === 'approved'
          );
          const approvedIds = approvedDerasars
            .map((d) => d.derasarId?.id || d.derasarId)
            .filter(Boolean)
            .map((id) => id.toString());
          const savedId = getStoredValue('activeDerasarId');

          if (savedId && approvedIds.includes(savedId)) {
            setActiveDerasarIdState(savedId);
          } else if (approvedIds.length > 0) {
            setActiveDerasarId(approvedIds[0]);
          } else {
            removeStoredValue('activeDerasarId');
            setActiveDerasarIdState(null);
          }
        })
        .catch(() => {
          clearUserState();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [applyTheme]);

  const loginUser = (userData) => {
    syncUserState(userData);

    if (userData.theme) {
      applyTheme(userData.theme);
    }

    const approved = (userData.derasars || []).filter((d) => d.status === 'approved');
    if (approved.length > 0) {
      const firstId = approved[0].derasarId?.id || approved[0].derasarId;
      setActiveDerasarId(firstId?.toString());
    }
  };

  const logout = () => {
    clearUserState();
  };

  const setActiveDerasarId = (id) => {
    if (!id) {
      removeStoredValue('activeDerasarId');
      setActiveDerasarIdState(null);
      setFinancialYears([]);
      setFinancialYearsLoaded(false);
      setActiveFinancialYearIdState(null);
      return;
    }

    setStoredValue('activeDerasarId', id);
    setActiveDerasarIdState(id);
  };

  const setActiveFinancialYearId = useCallback((id, derasarIdOverride = null) => {
    const targetDerasarId = (derasarIdOverride || activeDerasarId)?.toString();
    if (!targetDerasarId) {
      setActiveFinancialYearIdState(null);
      return;
    }

    const nextMap = { ...getStoredFinancialYearMap() };
    if (!id) {
      delete nextMap[targetDerasarId];
      setStoredFinancialYearMap(nextMap);
      setActiveFinancialYearIdState(null);
      return;
    }

    nextMap[targetDerasarId] = String(id);
    setStoredFinancialYearMap(nextMap);
    setActiveFinancialYearIdState(String(id));
  }, [activeDerasarId]);

  // Called after email is verified (updates local state without a full reload)
  const markEmailVerified = () => {
    const updated = { ...user, isEmailVerified: true };
    syncUserState(updated);
  };

  // Called after onboarding is completed
  const markProfileCompleted = () => {
    const updated = { ...user, profileCompleted: true };
    syncUserState(updated);
  };

  // Called after a password change — the old token is invalidated server-side
  // (passwordChangedAt), so callers must store the new one before making any
  // further authenticated request or they'll be logged out immediately.
  const updateToken = (token) => {
    const current = getStoredJson('user', {});
    syncUserState({ ...current, token });
  };

  const refreshUser = () =>
    getMe().then((profile) => {
      const current = getStoredJson('user', {});
      const updated = { ...profile, token: current.token };
      if (current.isEmailVerified) updated.isEmailVerified = true;
      syncUserState(updated);
    });

  const refreshFinancialYears = useCallback(async (derasarIdOverride = null) => {
    const targetDerasarId = (derasarIdOverride || activeDerasarId)?.toString();
    if (!targetDerasarId) {
      setFinancialYears([]);
      setFinancialYearsLoaded(false);
      setActiveFinancialYearIdState(null);
      return [];
    }

    const years = await getFinancialYears(targetDerasarId);
    const allYears = Array.isArray(years) ? years : [];
    const visibleYears = getVisibleFinancialYears(allYears);
    const storedId = getStoredFinancialYearMap()?.[targetDerasarId];
    const matchingStored = visibleYears.find((financialYear) => String(financialYear.id) === String(storedId));
    const fallback = getDefaultSelectableFinancialYear(visibleYears);
    const selectedId = matchingStored ? String(matchingStored.id) : (fallback ? String(fallback.id) : null);

    setFinancialYears(allYears);
    setFinancialYearsLoaded(true);
    setActiveFinancialYearIdState(selectedId);

    const nextMap = { ...getStoredFinancialYearMap() };
    if (selectedId) {
      nextMap[targetDerasarId] = selectedId;
    } else {
      delete nextMap[targetDerasarId];
    }
    setStoredFinancialYearMap(nextMap);

    return allYears;
  }, [activeDerasarId]);

  const activeDerasar = user?.derasars?.find((d) => {
    const dId = d.derasarId?.id || d.derasarId;
    return dId?.toString() === activeDerasarId;
  }) || null;
  const activeDerasarSubscription = activeDerasar?.derasarId?.subscription || null;
  const activeDerasarSubscriptionStatus = activeDerasarSubscription?.subscriptionStatus || null;
  const activeDerasarSubscriptionEndDate = activeDerasarSubscription?.endDate || null;
  const activeDerasarRegisteredSince = activeDerasarSubscription?.registeredSince || null;
  const activeDerasarHasPendingRenewal = !!activeDerasarSubscription?.renewalRequestedAt;
  const isActiveDerasarTrial = activeDerasarSubscription?.paymentMode === 'trial';
  const activeDerasarDaysUntilExpiry = activeDerasarSubscriptionEndDate
    ? Math.ceil((new Date(activeDerasarSubscriptionEndDate) - new Date()) / (1000 * 60 * 60 * 24))
    : null;
  const isActiveDerasarExpiringSoon =
    activeDerasarDaysUntilExpiry !== null &&
    activeDerasarDaysUntilExpiry >= 0 &&
    activeDerasarDaysUntilExpiry <= 30;
  const isActiveDerasarExpired =
    !!activeDerasarSubscription &&
    (
      activeDerasarSubscriptionStatus === 'expired' ||
      (activeDerasarSubscriptionEndDate && new Date(activeDerasarSubscriptionEndDate) <= new Date())
    );

  const approvedDerasars = (user?.derasars || []).filter((d) => d.status === 'approved');
  const visibleFinancialYears = getVisibleFinancialYears(financialYears);
  const activeFinancialYear = visibleFinancialYears.find(
    (financialYear) => String(financialYear.id) === String(activeFinancialYearIdState)
  ) || null;
  const openingFinancialYear = getOpeningFinancialYear(financialYears);

  const isDerasarAdmin =
    user?.role === 'derasar_admin' && activeDerasar?.role === 'admin';

  const activeGrantType = activeDerasar?.grantType || null;
  const activePermissions = activeDerasar?.permissions || null;
  // Owner = any admin of this derasar that is NOT a co_admin (registering admin or platform-acting admin)
  const isDerasarOwner = isDerasarAdmin && activeGrantType !== 'co_admin';

  const PERMISSION_LEVEL_RANK = { none: 0, view: 1, manage: 2 };
  // can(module, level): is the current user allowed this module at >= level in the active derasar?
  // Owners/platform admins => always true. Co-admins => checked against their permission map.
  const can = (module, level = 'view') => {
    if (!isDerasarAdmin) return false;
    if (activeGrantType !== 'co_admin') return true; // owner / platform-acting admin
    const held = (activePermissions || {})[module] || 'none';
    return (PERMISSION_LEVEL_RANK[held] || 0) >= (PERMISSION_LEVEL_RANK[level] || 0);
  };

  useEffect(() => {
    if (!activeDerasarId || !user) {
      setFinancialYears([]);
      setFinancialYearsLoaded(false);
      setActiveFinancialYearIdState(null);
      return;
    }

    refreshFinancialYears().catch(() => {
      setFinancialYears([]);
      setFinancialYearsLoaded(true);
      setActiveFinancialYearIdState(null);
    });
  }, [activeDerasarId, user, refreshFinancialYears]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        loginUser,
        logout,
        activeDerasarId,
        setActiveDerasarId,
        activeDerasar,
        activeDerasarSubscription,
        activeDerasarSubscriptionStatus,
        activeDerasarSubscriptionEndDate,
        activeDerasarRegisteredSince,
        activeDerasarHasPendingRenewal,
        isActiveDerasarTrial,
        activeDerasarDaysUntilExpiry,
        isActiveDerasarExpiringSoon,
        isActiveDerasarExpired,
        approvedDerasars,
        financialYears,
        financialYearsLoaded,
        visibleFinancialYears,
        openingFinancialYear,
        activeFinancialYearId: activeFinancialYearIdState,
        activeFinancialYear,
        setActiveFinancialYearId,
        refreshFinancialYears,
        isDerasarAdmin,
        isDerasarOwner,
        activeGrantType,
        activePermissions,
        can,
        markEmailVerified,
        markProfileCompleted,
        updateToken,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
